import React, { useState, useEffect } from 'react';
import { Briefcase, Save, Plus, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const JobListingTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobData, setJobData] = useState({
    title: '',
    jobType: '',
    location: '',
    compensation: '',
    overview: '',
    responsibilities: [''],
    whatWeOffer: [''],
    requirements: [''],
    idealCandidate: [''],
    inPersonEvent: {
      enabled: false,
      text: '',
      link: ''
    }
  });

  useEffect(() => {
    fetchJobData();
  }, []);

  const fetchJobData = async () => {
    try {
      const response = await axios.get(`${API}/content/job-listing`);
      if (response.data) {
        setJobData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch job data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/content/job-listing`, jobData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job listing saved successfully!');
    } catch (error) {
      toast.error('Failed to save job listing');
    } finally {
      setSaving(false);
    }
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...jobData[field]];
    newArray[index] = value;
    setJobData({ ...jobData, [field]: newArray });
  };

  const addArrayItem = (field) => {
    setJobData({ ...jobData, [field]: [...jobData[field], ''] });
  };

  const removeArrayItem = (field, index) => {
    const newArray = jobData[field].filter((_, i) => i !== index);
    setJobData({ ...jobData, [field]: newArray });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF4500]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#FF4500]" />
          Job Listing (Affiliates Page)
        </h3>
        <div className="flex items-center gap-3">
          <a
            href="/affiliates"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#0055FF] hover:text-[#0044CC] text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            Preview Page
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#FF4500] text-white px-4 py-2 rounded-lg hover:bg-[#E03E00] transition-all font-semibold disabled:opacity-50"
            data-testid="save-job-listing-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-lg mb-4">Basic Information</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
              <input
                type="text"
                value={jobData.title}
                onChange={(e) => setJobData({ ...jobData, title: e.target.value })}
                className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="e.g., Freelance Affiliate Marketing Partner"
                data-testid="job-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
              <input
                type="text"
                value={jobData.jobType}
                onChange={(e) => setJobData({ ...jobData, jobType: e.target.value })}
                className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="e.g., Freelance / Independent Contractor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={jobData.location}
                onChange={(e) => setJobData({ ...jobData, location: e.target.value })}
                className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="e.g., Remote (Work from anywhere)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Compensation</label>
              <input
                type="text"
                value={jobData.compensation}
                onChange={(e) => setJobData({ ...jobData, compensation: e.target.value })}
                className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="e.g., Commission-based"
              />
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-lg mb-4">Overview</h4>
          <textarea
            value={jobData.overview}
            onChange={(e) => setJobData({ ...jobData, overview: e.target.value })}
            className="w-full px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
            rows={4}
            placeholder="Describe the role and opportunity..."
            data-testid="job-overview-input"
          />
        </div>

        {/* Key Responsibilities */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-lg">Key Responsibilities</h4>
            <button
              onClick={() => addArrayItem('responsibilities')}
              className="flex items-center gap-1 text-[#0055FF] hover:text-[#0044CC] text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-3">
            {jobData.responsibilities.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleArrayChange('responsibilities', index, e.target.value)}
                  className="flex-1 h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter responsibility..."
                />
                {jobData.responsibilities.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('responsibilities', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* What We Offer */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-lg">What We Offer</h4>
            <button
              onClick={() => addArrayItem('whatWeOffer')}
              className="flex items-center gap-1 text-[#0055FF] hover:text-[#0044CC] text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-3">
            {jobData.whatWeOffer.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleArrayChange('whatWeOffer', index, e.target.value)}
                  className="flex-1 h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter benefit..."
                />
                {jobData.whatWeOffer.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('whatWeOffer', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-lg">Requirements</h4>
            <button
              onClick={() => addArrayItem('requirements')}
              className="flex items-center gap-1 text-[#0055FF] hover:text-[#0044CC] text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-3">
            {jobData.requirements.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleArrayChange('requirements', index, e.target.value)}
                  className="flex-1 h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter requirement..."
                />
                {jobData.requirements.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('requirements', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Ideal Candidate */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-lg">Ideal Candidate</h4>
            <button
              onClick={() => addArrayItem('idealCandidate')}
              className="flex items-center gap-1 text-[#0055FF] hover:text-[#0044CC] text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-3">
            {jobData.idealCandidate.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleArrayChange('idealCandidate', index, e.target.value)}
                  className="flex-1 h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Enter trait..."
                />
                {jobData.idealCandidate.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('idealCandidate', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* In-Person Event */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-lg mb-4">In-Person Event (Optional)</h4>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={jobData.inPersonEvent?.enabled || false}
                onChange={(e) => setJobData({
                  ...jobData,
                  inPersonEvent: { ...jobData.inPersonEvent, enabled: e.target.checked }
                })}
                className="w-5 h-5 rounded border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
              />
              <span className="text-sm font-medium text-gray-700">Show in-person event option</span>
            </label>
            
            {jobData.inPersonEvent?.enabled && (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Description</label>
                  <input
                    type="text"
                    value={jobData.inPersonEvent?.text || ''}
                    onChange={(e) => setJobData({
                      ...jobData,
                      inPersonEvent: { ...jobData.inPersonEvent, text: e.target.value }
                    })}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                    placeholder="e.g., Visit our upcoming Masterclass on May 16th"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Link</label>
                  <input
                    type="url"
                    value={jobData.inPersonEvent?.link || ''}
                    onChange={(e) => setJobData({
                      ...jobData,
                      inPersonEvent: { ...jobData.inPersonEvent, link: e.target.value }
                    })}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                    placeholder="https://events.mnaconsult.com/"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#FF4500] text-white px-6 py-3 rounded-lg hover:bg-[#E03E00] transition-all font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Job Listing
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobListingTab;
